import {
  type CSSProperties,
  Fragment,
  type ReactNode,
  forwardRef,
  useId,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { XIcon } from "lucide-react";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";
import { FOCUS_RING } from "simplydo/react/ui/utils";
import type { UiColor } from "simplydo/react/ui/utils/colors";

import util from "utils/utils";

import { RoleLabel } from "components/lib/Roles/RoleLabel";
import { getRoleIcon } from "components/lib/Roles/roleIcons";

import { SocialButtons, SocialRow, SocialStack } from "./SocialLinks";
import type { ChipSocialLink } from "./useSocialLinks";

const StyledTitle = styled.div`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: bold !important;
  font-size: 13px !important;
  line-height: 15px !important;
  text-decoration: none !important;
  width: fit-content;
  max-width: 100%;
  position: relative;
  color: black !important;
  transition: color 0.12s;

  &.is-compact {
    font-size: 12px !important;
    line-height: 14px !important;
  }

  &.is-very-compact {
    font-weight: inherit !important;
    font-size: 1em !important;
    line-height: 1lh !important;
    color: inherit !important;
  }

  &.is-inverted {
    color: white !important;
  }
`;

const StyledAvatar = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 35px;
  width: 35px;
  min-height: 35px;
  min-width: 35px;
  border-radius: 50%;
  background-color: #ededed;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  margin-right: 8px !important;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);

  &.has-image {
    background-color: transparent;
  }

  &.is-tall {
    height: 40px;
    width: 40px;
    min-height: 40px;
    min-width: 40px;
  }

  &.is-compact {
    height: 24px;
    width: 24px;
    min-height: 24px;
    min-width: 24px;
  }

  &.is-very-compact {
    display: inline-block;
    transform: translateY(10%);
    height: 1em;
    width: 1em;
    min-height: 1em;
    min-width: 1em;
    margin-right: 4px !important;
    box-shadow: none;
  }

  &.has-custom-radius {
    border-radius: var(--chip-avatar-radius);
  }

  &.has-custom-background-size {
    background-size: var(--chip-avatar-background-size);
  }
`;

const RoleBadges = styled.div`
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: flex;

  &.is-compact {
    right: -3px;
  }
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  color: #555;

  & + & {
    margin-left: -6px;
  }

  .is-compact > & {
    width: 13px;
    height: 13px;
    font-size: 6px;
  }

  .is-compact > & + & {
    margin-left: -5px;
  }

  .is-large > & {
    width: 20px;
    height: 20px;
    font-size: 9px;
  }

  .is-large > & + & {
    margin-left: -7px;
  }
`;

const StyledChip = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border-radius: 50px;
  padding: 3px 16px 3px 3px;
  transition:
    background 0.12s,
    box-shadow 0.12s;

  &:focus-visible {
    ${FOCUS_RING}
  }

  &.has-stack {
    padding-right: 6px;
  }

  &.is-fluid {
    display: flex;
    width: 100%;
  }

  &.is-very-compact {
    display: inline-flex;
    align-items: baseline;
    min-width: 0;
    padding: 0px;
  }

  &.is-inverted {
    background: rgba(0, 3, 45, 0.3);
  }

  &.has-custom-radius {
    border-radius: var(--chip-border-radius);
  }

  &.is-interactive:not(.is-very-compact):hover {
    background: var(--chip-primary-softer);
    box-shadow: inset 0 0 0 1px var(--chip-primary-soft);

    ${StyledTitle} {
      color: var(--chip-primary-ink) !important;
    }
  }

  &.is-interactive:not(.is-very-compact):active {
    background: var(--chip-primary-soft);
    box-shadow: inset 0 0 0 1px var(--chip-primary-soft);

    ${StyledTitle} {
      color: var(--chip-primary-ink) !important;
    }
  }

  &.is-interactive.is-inverted:not(.is-very-compact):hover {
    background: rgba(255, 255, 255, 0.14);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);

    ${StyledTitle} {
      color: white !important;
    }
  }

  &.is-interactive.is-inverted:not(.is-very-compact):active {
    background: rgba(255, 255, 255, 0.22);

    ${StyledTitle} {
      color: white !important;
    }
  }

  &.is-interactive.is-very-compact {
    border-radius: 50px 16px 16px 50px;
    line-height: 1.2em;
  }

  &.is-interactive.is-very-compact:hover {
    background: var(--chip-primary-softer);

    ${StyledTitle} {
      color: var(--chip-primary-ink) !important;
    }
  }

  &.is-interactive.is-very-compact:active {
    background: var(--chip-primary-soft);
  }

  &.is-interactive.is-very-compact.is-inverted:hover {
    background: rgba(255, 255, 255, 0.14);

    ${StyledTitle} {
      color: white !important;
    }
  }

  &.is-interactive.is-very-compact.is-inverted:active {
    background: rgba(255, 255, 255, 0.22);
  }
`;

const ChipTextContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  user-select: none;
  text-wrap: nowrap;
  min-width: 0px;
  flex: 1 1 auto;
`;

const StyledPostText = styled.span`
  cursor: default;
  font-size: 13px !important;
  vertical-align: middle;
  color: black;
  margin-left: 6px;

  &.is-inverted {
    color: white;
  }
`;

const StyledDescription = styled.p`
  font-size: 11px !important;
  line-height: 13px;
  min-height: 10px;
  min-width: 1px;
  text-overflow: ellipsis;
  overflow: hidden;
  font-weight: normal !important;
  color: black !important;
  margin: 0px !important;

  &.is-compact {
    font-size: 10px !important;
  }

  &.is-inverted {
    color: white !important;
  }

  &.is-trigger {
    max-width: 150px;
  }
`;

const ActionIcon = styled(Icon)`
  color: gray;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 4px;
  border-radius: 50%;

  &:hover {
    background: gray;
    color: white;
  }

  &.is-inverted {
    color: white;

    &:hover {
      background: white;
      color: gray;
    }
  }
`;

const PopupCard = styled.div`
  position: relative;
  width: 360px;
  max-width: 100%;
  border-radius: 12px;
  background: white;
  box-shadow:
    0 0 0 1px rgba(15, 23, 42, 0.08),
    0 8px 24px rgba(15, 23, 42, 0.12),
    0 2px 6px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  font-size: 13px;
  line-height: 1.4;
  color: #172033;
  text-align: left;
  white-space: normal;
`;

const PopupBody = styled.div`
  padding: 16px 16px 14px;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding-right: 24px;
`;

/* Positioned against the popup card. */
const CloseButton = styled(Button)`
  position: absolute;
  top: 8px;
  right: 8px;
  margin: 0;
`;

const PopupAvatar = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 56px;
  height: 56px;
  color: ${({ theme }) => theme.primaryColour};
  border-radius: 50%;
  background-color: #ededed;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow:
    0 0 0 3px white,
    0 1px 4px 3px rgba(0, 0, 0, 0.12);

  &.has-image {
    background-color: transparent;
  }

  &.has-custom-radius {
    border-radius: var(--chip-avatar-radius);
  }

  &.has-custom-background-size {
    background-size: var(--chip-avatar-background-size);
  }
`;

const PopupTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const PopupSubtitle = styled.div`
  font-size: 12.5px;
  color: #4a5568;
`;

const PopupAbout = styled.p`
  margin: 10px 0 0;
  font-size: 12px;
  color: #6b7486;
`;

const PopupContent = styled.div`
  margin-top: 10px;
`;

const SectionLabel = styled.div`
  margin: 12px 0 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a93a5;
`;

const RolePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 4px 12px;
  margin: 0;
  font-size: 12px;

  dt {
    color: #6b7486;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const ActionList = styled.div`
  padding: 4px;
  border-top: 1px solid #eef1f5;
`;

const ActionRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 12px;
  border: 0;
  border-radius: 6px;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--chip-primary-softer);
    color: var(--chip-primary-ink);
  }

  &:focus-visible {
    ${FOCUS_RING}
  }

  &.is-danger:hover {
    background: rgba(180, 35, 24, 0.08);
    color: #b42318;
  }
`;

const getCompactClass = (compact?: boolean | "very") =>
  compact === "very" ? "is-very-compact" : compact ? "is-compact" : undefined;

const getAvatarStyle = ({
  imageUrl,
  avatarBorderRadius,
  avatarBackgroundSize,
}: {
  imageUrl?: string;
  avatarBorderRadius?: number;
  avatarBackgroundSize?: string;
}): CSSProperties => ({
  ...(imageUrl ? { backgroundImage: `url(${imageUrl})` } : {}),
  ...(avatarBorderRadius !== undefined
    ? { "--chip-avatar-radius": `${avatarBorderRadius}px` }
    : {}),
  ...(avatarBackgroundSize
    ? { "--chip-avatar-background-size": avatarBackgroundSize }
    : {}),
});

type ChipLabel = { name?: string; icon?: { name?: string; colour?: string } };

export type ChipAction = {
  key: string;
  label: string;
  icon: IconData;
  onClick: () => void;
  danger?: boolean;
};

export type ChipDetail = { label: string; value: ReactNode };

export type ChipConfiguration = {
  style?: CSSProperties;
  avatarBorderRadius?: number;
  avatarBackgroundSize?: string;
  chipBorderRadius?: number;
  inverted?: boolean;
  content?: React.ReactNode;
  labels?: ChipLabel[];

  precedingText?: string;
  postText?: string;
  target?: "_blank";

  onClick?:
    | ((
        e:
          | React.MouseEvent<HTMLDivElement>
          | React.KeyboardEvent<HTMLDivElement>,
      ) => void)
    | undefined;
  /** Fully inert: no popup, no onClick, no action icon, not focusable.
   *  Clicks are left for the parent, so the chip can sit inside a menu item. */
  disabled?: boolean;
  /** Only turns off the profile popup; onClick still works. */
  popupDisabled?: boolean;
  compact?: boolean | "very";
  /** Stretches the chip to fill its container, e.g. a table column. */
  fluid?: boolean;
  actionIcon?: IconData;
  actionClick?: () => void;
  actionTooltip?: string;
};

type BaseChipProps = (
  | { imageUrl: string; icon?: never }
  | { imageUrl?: never; icon?: IconData }
  | { imageUrl?: never; icon?: never }
) & {
  title: React.ReactNode;
  subtitle?: string;
  /** Longer text shown under the header in the popup. */
  about?: string;
  details?: ChipDetail[];
  actions?: ChipAction[];
  socials?: ChipSocialLink[];
  /** Shows socials on the chip itself; the popup always shows them. */
  showSocials?: boolean;
} & ChipConfiguration;

const RoleBadgeIcons = ({
  labels,
  size,
}: {
  labels: ChipLabel[];
  size: "large" | "default" | "compact";
}) => {
  const roles = labels.filter((label) => getRoleIcon(label.icon?.name));
  if (!roles.length) return null;
  const shown = roles.slice(0, roles.length > 2 ? 1 : 2);
  const iconPx = { large: 12, default: 10, compact: 8 }[size];
  return (
    <RoleBadges
      className={cx(
        size === "large" && "is-large",
        size === "compact" && "is-compact",
      )}
    >
      {shown.map((label) => (
        <RoleBadge key={`${label.name}-${label.icon?.name}`}>
          <Icon
            icon={getRoleIcon(label.icon?.name)}
            color={label.icon?.colour as UiColor}
            style={{ width: iconPx, height: iconPx }}
          />
        </RoleBadge>
      ))}
      {roles.length > 2 ? <RoleBadge>+{roles.length - 1}</RoleBadge> : null}
    </RoleBadges>
  );
};

const BaseChip = forwardRef<HTMLAnchorElement, BaseChipProps>(
  (
    {
      imageUrl,
      icon,
      avatarBorderRadius,
      avatarBackgroundSize,
      chipBorderRadius,
      style,
      disabled,
      popupDisabled,
      inverted,
      title,
      subtitle,
      about,
      content,
      details,
      actions,
      labels,
      socials,
      showSocials,
      precedingText,
      postText,
      onClick,
      compact,
      fluid,
      actionIcon,
      actionClick,
      actionTooltip,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const chipId = useId();
    const theme = useTheme();
    const [open, setOpen] = useState(false);

    const handleClickWithDefault = (e: React.MouseEvent<HTMLDivElement>) => {
      if (onClick && !disabled) {
        e.stopPropagation();
        e.preventDefault();
        onClick(e);
      }
    };

    // Enter / Space run onClick. Without onClick the key event bubbles to the
    // Popup trigger, which opens the popup like a click does. Keys from nested
    // links (socials) are left alone so they still open.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        e.target !== e.currentTarget ||
        disabled ||
        !onClick ||
        (e.key !== "Enter" && e.key !== " ")
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    };

    const hasPopup = !disabled && !popupDisabled;
    // A focusable button only when the chip does something.
    const focusable = !disabled && (hasPopup || !!onClick);

    const imageUrlFallback = imageUrl
      ? util.mixinCssUrlFallback(imageUrl, util.avatarUrl() ?? "")
      : undefined;

    const veryCompact = compact === "very";
    const compactClass = getCompactClass(compact);
    const roleLabels = labels ?? [];
    const roleNames = roleLabels
      .map((label) => label.name)
      .filter(Boolean)
      .join(", ");
    const socialLinks = socials ?? [];
    const showSocialRow = showSocials && !compact && socialLinks.length > 0;
    const showSocialStack =
      showSocials &&
      compact === true &&
      socialLinks.some((link) => !link.internal);
    const primary = theme.colors.primary;

    const avatarStyle = getAvatarStyle({
      imageUrl: imageUrlFallback,
      avatarBorderRadius,
      avatarBackgroundSize,
    });
    const chipStyle = {
      "--chip-primary-softer": primary.softer,
      "--chip-primary-soft": primary.soft,
      "--chip-primary-ink": primary.ink,
      ...(chipBorderRadius !== undefined
        ? { "--chip-border-radius": `${chipBorderRadius}px` }
        : {}),
    } as CSSProperties;

    const closeThen = (action: ChipAction) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      action.onClick();
    };

    return (
      <span
        className="chip"
        ref={ref}
        style={{
          display: fluid && !veryCompact ? "flex" : "inline-flex",
          flexWrap: "wrap",
          alignItems: "center",
          minWidth: 0,
          whiteSpace: "nowrap",
          color: "inherit",
          maxWidth: "100%",
          verticalAlign: veryCompact ? "baseline" : "middle",
          cursor: disabled ? "inherit" : focusable ? "pointer" : "default",
          ...(fluid && !veryCompact ? { width: "100%" } : {}),
          ...(style || {}),
        }}
        onClick={(e) => {
          // Disabled chips are inert. Stopping the click here would make
          // the parent (for example a dropdown item) impossible to activate.
          if (disabled) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Popup
          on="click"
          transparent
          trapFocus
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disabled={!hasPopup || !!onClick}
          trigger={
            <StyledChip
              onClick={handleClickWithDefault}
              onKeyDown={handleKeyDown}
              tabIndex={focusable ? 0 : undefined}
              role={focusable ? "button" : undefined}
              as={veryCompact ? "span" : "div"}
              className={cx(
                compactClass,
                inverted && "is-inverted",
                chipBorderRadius !== undefined && "has-custom-radius",
                focusable && "is-interactive",
                showSocialStack && "has-stack",
                fluid && !veryCompact && "is-fluid",
              )}
              style={chipStyle}
            >
              <StyledAvatar
                as={veryCompact ? "span" : "div"}
                className={cx(
                  compactClass,
                  showSocialRow && "is-tall",
                  imageUrlFallback && "has-image",
                  avatarBorderRadius !== undefined && "has-custom-radius",
                  avatarBackgroundSize && "has-custom-background-size",
                )}
                style={avatarStyle}
                title={!veryCompact && roleNames ? roleNames : undefined}
              >
                {!imageUrl && icon ? (
                  <Icon
                    icon={icon}
                    style={{ marginLeft: 2, width: 10 }}
                    size={veryCompact ? "small" : undefined}
                  />
                ) : null}
                {!veryCompact ? (
                  <RoleBadgeIcons
                    labels={roleLabels}
                    size={compact ? "compact" : "default"}
                  />
                ) : null}
              </StyledAvatar>
              <ChipTextContainer
                as={veryCompact ? "span" : "div"}
                className={cx(compactClass)}
              >
                {!veryCompact && precedingText ? (
                  <StyledDescription
                    className={cx(inverted && "is-inverted", "is-trigger")}
                  >
                    {precedingText}
                  </StyledDescription>
                ) : null}
                {showSocialRow ? <SocialRow links={socialLinks} /> : null}
                <StyledTitle
                  id={`${chipId}title`}
                  as={veryCompact ? "span" : "div"}
                  className={cx(compactClass, inverted && "is-inverted")}
                >
                  {title}
                </StyledTitle>
                {!veryCompact && subtitle ? (
                  <StyledDescription
                    className={cx(
                      compactClass,
                      inverted && "is-inverted",
                      "is-trigger",
                    )}
                  >
                    {subtitle}
                  </StyledDescription>
                ) : null}
              </ChipTextContainer>
              {showSocialStack ? (
                <SocialStack
                  links={socialLinks}
                  className={cx(fluid && "is-fluid")}
                />
              ) : null}
              {!veryCompact && !disabled && actionIcon && actionClick ? (
                <Popup
                  disabled={!actionTooltip}
                  trigger={
                    <ActionIcon
                      icon={actionIcon}
                      className={cx(inverted && "is-inverted")}
                      role="button"
                      tabIndex={0}
                      id={`${chipId}action`}
                      aria-label={
                        actionTooltip ??
                        (actionIcon === XIcon ? t("chips.remove") : undefined)
                      }
                      aria-labelledby={`${chipId}action ${chipId}title`}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        actionClick();
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key !== "Enter" && e.key !== " ") {
                          return;
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        actionClick();
                      }}
                    />
                  }
                >
                  {actionTooltip}
                </Popup>
              ) : null}
            </StyledChip>
          }
        >
          <PopupCard
            style={
              {
                "--chip-primary-softer": primary.softer,
                "--chip-primary-soft": primary.soft,
                "--chip-primary-ink": primary.ink,
              } as CSSProperties
            }
          >
            <PopupBody>
              <PopupHeader>
                <PopupAvatar
                  className={cx(
                    imageUrlFallback && "has-image",
                    avatarBorderRadius !== undefined && "has-custom-radius",
                    avatarBackgroundSize && "has-custom-background-size",
                  )}
                  style={getAvatarStyle({
                    imageUrl: imageUrlFallback,
                    avatarBorderRadius,
                    avatarBackgroundSize,
                  })}
                  title={roleNames || undefined}
                >
                  {!imageUrl && icon ? <Icon icon={icon} size="large" /> : null}
                  <RoleBadgeIcons labels={roleLabels} size="large" />
                </PopupAvatar>
                <div style={{ minWidth: 0 }}>
                  {socialLinks.length ? (
                    <div style={{ marginBottom: 4 }}>
                      <SocialButtons links={socialLinks} />
                    </div>
                  ) : null}
                  <PopupTitle>{title}</PopupTitle>
                  {subtitle ? <PopupSubtitle>{subtitle}</PopupSubtitle> : null}
                </div>
              </PopupHeader>
              {about ? <PopupAbout>{about}</PopupAbout> : null}
              {content ? <PopupContent>{content}</PopupContent> : null}
              {roleLabels.some((label) => label.name) ? (
                <>
                  <SectionLabel>{t("users.chip.roles")}</SectionLabel>
                  <RolePills>
                    {roleLabels.map((label) => (
                      <RoleLabel
                        key={`${label.name}-${label.icon?.name}-${label.icon?.colour}`}
                        role={label}
                      />
                    ))}
                  </RolePills>
                </>
              ) : null}
              {details?.length ? (
                <>
                  <SectionLabel>{t("generic.details")}</SectionLabel>
                  <DetailGrid>
                    {details.map(({ label, value }, index) => (
                      <Fragment key={`${label}-${index}`}>
                        <dt>{label}</dt>
                        <dd>{value}</dd>
                      </Fragment>
                    ))}
                  </DetailGrid>
                </>
              ) : null}
            </PopupBody>
            {actions?.length ? (
              <ActionList>
                {actions.map((action) => (
                  <ActionRow
                    key={action.key}
                    type="button"
                    className={cx(action.danger && "is-danger")}
                    onClick={closeThen(action)}
                  >
                    <Icon icon={action.icon} />
                    {action.label}
                  </ActionRow>
                ))}
              </ActionList>
            ) : null}
            {/* Last in DOM so Tab reaches the content first; Escape also closes. */}
            <CloseButton
              variant="ghost"
              size="small"
              circular
              aria-label={t("generic.close")}
              onClick={() => setOpen(false)}
            >
              <Icon icon={XIcon} />
            </CloseButton>
          </PopupCard>
        </Popup>
        {postText && (
          <StyledPostText className={cx(inverted && "is-inverted")}>
            {postText}
          </StyledPostText>
        )}
      </span>
    );
  },
);

BaseChip.displayName = "BaseChip";

export default BaseChip;
